import { useCallback, useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import type { Dict } from 'mixpanel-browser'
import {
  pageView,
  track,
  TSegmentPageviewProps,
  TSegmentTrackProps,
} from '../../utils/common/web/analytics'
import { /*gaEvent,*/ TGAProps } from '../../utils/common/web/ga-event'
import {
  TGtmEventParameter,
  TGtm4Event,
} from '../../utils/common/web/gtm-events/types'
import useGtmEvent from '@/hooks/analytics/use-gtm-event'
import {
  TMixPanelEventPayload,
  mixpanelPageView,
  mixpanelTrack,
} from '@/root/utils/common/web/mixpanel-events'
import { trackCustomGTMEvents } from '@/root/utils/tracking'
import { getFormattedEventLabel } from '@/root/utils/common/web/get-formatted-event-label'
import { getPageSourceFromPath } from '@/root/utils/common/functions/get-category-from-path'

export type TAnalyticsPushEvent = {
  gaPayload?: TGAProps
  gtmPayload?: TGtmEventParameter
  gtm4Payload?: TGtm4Event
  segmentTrackPayload?: TSegmentTrackProps
  segmentPageViewPayload?: TSegmentPageviewProps
  mixpanelEventPayload?: TMixPanelEventPayload
}

export const useAnalyticsEvent = () => {
  const gtmEvent = useGtmEvent()
  const router = useRouter()
  const path = router.route
  const pushEvent = useCallback(
    (sourceData: TAnalyticsPushEvent) => {
      const {
        gaPayload,
        gtmPayload,
        gtm4Payload,
        segmentTrackPayload,
        segmentPageViewPayload,
        mixpanelEventPayload,
      } = sourceData

      if (process.env.NODE_ENV === 'development') {
        console.info(sourceData)
      } else {
        if (gaPayload) {
          // gaEvent(gaPayload)
          return
        }

        if (gtmPayload) {
          return
        }

        if (gtm4Payload) {
          const eventData = gtm4Payload.eventData
          let formattedLabel = '',
            pageSource = eventData.pageSource ?? ''
          const label = eventData.eventLabel ?? ''
          formattedLabel = getFormattedEventLabel(label)
          if (!pageSource && path) {
            pageSource = getPageSourceFromPath(path)
          }

          trackCustomGTMEvents({
            eventData: { ...eventData, eventLabel: formattedLabel, pageSource },
          })
        }

        if (segmentTrackPayload) {
          track(segmentTrackPayload)
        }

        if (segmentPageViewPayload) {
          pageView(segmentPageViewPayload)
        }

        if (mixpanelEventPayload) {
          mixpanelTrack(mixpanelEventPayload)
        }
      }
    },
    [gtmEvent],
  )

  return pushEvent
}

// we don't really need a hook for analytics events (do we?), that's why not using `useAnalyticsEvent`
type TNewAnalyticsArg =
  | { type: 'mixpanel'; data: TMixPanelEventPayload }
  | { type: 'something-else'; data: never }

export function newPushToAnalytics(options: TNewAnalyticsArg) {
  if (options.type === 'mixpanel') {
    mixpanelTrack(options.data)
  }

  // keep adding other analytics providers in the future ( hopefully wouldn't need to)
}

// keeping `sourceData` optional gives us a way to tell `usePageView` to call again
// until sourceData is found. For example if you are depending on 5 'mandatory' values
// to be sent in sourceData, you can keep the `sourceData` argument passed to the hook as
// null or undefined until all those 5 values become available
type TNewArg1PageViewAnalysticsType = 'mixpanel'

export function usePageView(
  arg1?: TNewArg1PageViewAnalysticsType | TAnalyticsPushEvent,
  arg2?: {
    pageOrScreenName: string
    payload: Dict
  },
) {
  let sourceData: TAnalyticsPushEvent | undefined

  if (typeof arg1 !== 'string') {
    sourceData = arg1
  }

  const pushEvent = useAnalyticsEvent()
  const firedOnce = useRef(false)

  // this caters to the old implementation which depends on the fact
  // that you have to pass the argument that return value function of `useAnalyticsEvent`
  // takes - which can take any type of data like ga, segment event, or pageview which doesn't make sense —
  useEffect(() => {
    if (!firedOnce.current && sourceData) {
      pushEvent(sourceData)
      firedOnce.current = true
    }
  }, [pushEvent, sourceData])

  // -----------------------------------------------------------------------------------------------------//
  //         The following caters to the new implementation which depends on the fact `usePageView`
  //                         will only ever fire pageviews and not events
  // -----------------------------------------------------------------------------------------------------//

  // if pageview options becomes undefined, reset.
  useEffect(() => {
    if (!arg2) {
      firedOnce.current = false
    }
  }, [arg2])

  useEffect(() => {
    if (!firedOnce.current && arg2 && arg2.pageOrScreenName) {
      if (arg1 === 'mixpanel') {
        mixpanelPageView(arg2.pageOrScreenName, arg2.payload)
      }
      firedOnce.current = true
    }
  }, [arg1, arg2])
}
